import { TellescopeModelFromName, useTellescopeMutation } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { pendo } from "../../../../../tracking";
import {
  MessageCompositionFooter,
  MessageCompositionFooterPayload,
} from "../../../../../components/MessageCompositionFooter";
import { buildSmsMessageWithFiles } from "../../../utils";

export interface InboxSMSCompositionFooterProps {
  enduserId?: string;
  toNumber?: string;
  fromNumber?: string;
  onSend?: (sms: TellescopeModelFromName<"sms_messages">) => void;
}

export const InboxSMSCompositionFooter = ({
  enduserId,
  toNumber,
  fromNumber,
  onSend,
}: InboxSMSCompositionFooterProps) => {
  const { t } = useTranslation("inbox");

  const sendToEnduserMutation = useTellescopeMutation({
    model: "sms_messages",
    method: "createOne",
    onSuccess: (res) => onSend?.(res),
    onError: (error) => console.error("Failed to send SMS message:", error),
  });

  const createEnduserMutation = useTellescopeMutation({
    model: "endusers",
    method: "createOne",
    onError: (error) => console.error("Failed to create enduser:", error),
  });

  const handleSend = ({ message, files }: MessageCompositionFooterPayload, sendAt?: Date) => {
    const fullMessage = buildSmsMessageWithFiles(message, files);

    if (enduserId) {
      sendToEnduserMutation.mutate({
        message: fullMessage,
        enduserId,
        enduserPhoneNumber: toNumber,
        phoneNumber: fromNumber,
        ...(sendAt && { sendAt }),
        inbound: false,
      });
    } else {
      createEnduserMutation.mutate(
        { phone: toNumber },
        {
          onSuccess: (res) =>
            sendToEnduserMutation.mutate({
              message: fullMessage,
              enduserId: res.id,
              enduserPhoneNumber: toNumber,
              phoneNumber: fromNumber,
              ...(sendAt && { sendAt }),
              inbound: false,
            }),
        }
      );
    }
  };

  const canSend = !sendToEnduserMutation.isPending && !createEnduserMutation.isPending;

  return (
    <MessageCompositionFooter
      placeholder={t("Type your message...")}
      canSend={canSend}
      enduserId={enduserId}
      channelType="sms"
      onSubmit={(payload) => {
        pendo.track("new-sms-thread-send");
        handleSend(payload);
      }}
      onSchedule={(payload, sendAt) => handleSend(payload, sendAt)}
      trackingIds={{ schedule: "sms-compose-schedule-message-dialog-open" }}
    />
  );
};
